/**
 * English Translations (Default)
 *
 * This is the base translation file. All other languages should follow this structure.
 */

import type { Translations } from '../types';

export const en: Translations = {
  language: 'en',
  languageName: 'English',

  common: {
    demo: 'DEMO',
    privacy: 'Privacy',
    terms: 'Terms',
  },

  privacy: {
    title: 'Privacy Policy',
    whatWeStore: 'What we store',
    whatWeStoreDesc:
      'We temporarily process facial geometry for age estimation. No images or biometric templates are stored after verification completes.',
    whatWeDont: "What we don't",
    whatWeDontDesc:
      'We do not store photos, videos, or biometric identifiers. Your face data is processed in-browser and discarded immediately.',
    whyWeUseIt: 'Why we use it',
    whyWeUseItDesc:
      'Age estimation helps websites comply with age-gating regulations without requiring personal documents or accounts.',
    readFull: 'Read our full',
    privacyPolicyLink: 'Privacy Policy',
  },

  theme: {
    system: 'System',
    light: 'Light',
    dark: 'Dark',
  },

  face: {
    noFace: 'No face detected',
    tooFar: 'Face too far',
    tooClose: 'Face too close',
    offCenter: 'Face off-center',
    poorLighting: 'Poor lighting',
    good: 'Analyzing, please hold...',
    holdStill: 'Analyzing, please hold...',
    holdStillCountdown: 'Hold still... {count}',
    processing: 'Processing...',
    looking: 'No face detected',
    centerFace: 'Face off-center',
    moveCloser: 'Face too far',
    moveBack: 'Face too close',
    keepInPosition: 'Hold position',
    perfect: 'Perfect',
    centerInFrame: 'Place your head within the frame',
    outsideOval: 'Face outside oval',
  },

  liveness: {
    blink: {
      title: 'Blink Your Eyes',
      hint: 'Slowly blink once',
    },
    smile: {
      title: 'Smile',
      hint: 'Show a natural smile',
    },
    lookLeft: {
      title: 'Turn Left',
      hint: 'Slowly turn your head left',
    },
    lookRight: {
      title: 'Turn Right',
      hint: 'Slowly turn your head right',
    },
    openMouth: {
      title: 'Open Your Mouth',
      hint: 'Open your mouth wide',
    },
    lookUp: {
      title: 'Look Up',
      hint: 'Tilt your head up',
    },
    lookDown: {
      title: 'Look Down',
      hint: 'Tilt your head down',
    },
    raiseBrows: {
      title: 'Raise Eyebrows',
      hint: 'Raise your eyebrows',
    },
    improveLighting: {
      title: 'Improve Lighting',
      hint: 'Move to a brighter area to continue',
    },
  },

  document: {
    selectType: 'Select your document type',
    captureFront: 'Capture front of document',
    captureBack: 'Capture back of document',
    processing: 'Processing your document...',
    tips: {
      goodLighting: 'Ensure good lighting',
      flatSurface: 'Place document on a flat surface',
      allCorners: 'Make sure all corners are visible',
      noGlare: 'Avoid glare and shadows',
    },
    types: {
      passport: 'Passport',
      idCard: 'ID Card',
      driversLicense: "Driver's License",
    },
  },

  ui: {
    continue: 'Continue',
    next: 'Next',
    retry: 'Try Again',
    startOver: 'Start Over',
    cancel: 'Cancel',
    success: 'Age Check Passed',
    failure: 'Age Check Failed',
    loading: 'Loading...',
    error: 'An error occurred',
    permissionRequired: 'Permission Required',
    cameraPermission: 'Camera access is required',
    goBack: 'Go back',
    switchCamera: 'Switch camera',
    completeVerification: 'Complete',
    continueToId: 'Continue to ID Check',
  },

  screens: {
    welcome: {
      title: '18+ Age Check',
      subtitle: 'Anonymous Age Check',
      startButton: 'Start',
      secureNote: 'Secure & Encrypted',
      ageTechBy: 'Age tech by AppBouncer',
      waitingCamera: 'Waiting for Camera Access',
      allowCamera: 'Allow Camera Access',
      noBiometric: 'No biometric data stored',
      loading: 'Loading...',
      loadingRequirements: 'Loading requirements...',
    },
    permissions: {
      title: 'Loading ...',
      description: 'Please allow camera access when prompted',
      allowButton: 'Allow Camera Access',
      privacyNote: 'Your data is encrypted and secure',
      goodLighting: 'Good Lighting!',
      goodLightingHint: 'Ensure your face is well-lit',
      getReady: 'Get Ready',
      removeCoverings: 'Remove Face Coverings',
      removeGlasses: 'Remove Glasses',
    },
    facePosition: {
      title: 'Position Your Face',
      hint: 'Place your head within the frame',
      step: 'Step',
      stepOf: 'Step {current} of {total}',
      lookingForFace: 'Looking for face...',
    },
    livenessCheck: {
      title: 'Liveness Check',
      followInstructions: 'Follow the instructions',
      preparing: 'Preparing...',
      complete: 'Liveness Verified!',
      actionAutoDetected: 'Action will be detected automatically',
      failed: 'Liveness Check Failed',
      failedDescription:
        "We couldn't verify your liveness. Please ensure good lighting, face the camera directly, and follow the on-screen instructions.",
      retryButton: 'Retry Liveness Check',
      restartButton: 'Start Over',
    },
    ageEstimation: {
      title: 'Hold Still',
      analyzing: 'Analyzing...',
      analyzingProgress: 'Analyzing face {current} of {total}...',
      estimatedAge: 'Estimated Age',
      holdStill: 'Hold Still',
      verified: 'Age Verified',
      verificationUncertain: 'Age Uncertain',
      estimationComplete: 'Age check complete',
      confirmedAge: 'Confirmed {minAge}+ years old',
      couldNotConfirm: 'Could not confirm {minAge}+ age',
      ageConfirmed: 'Age Confirmed',
      aboveMinimumAge: 'You meet the minimum age requirement',
      belowMinimumAge: 'Below minimum age',
      documentRequired: 'Document Required',
      uploadDocumentMessage: 'Please upload a valid ID document to verify your age',
      idHint: "ID card, passport, or driver's license",
      confidenceLevels: 'Confidence by Age Threshold',
      confidence: 'confidence',
      years: 'years',
      continueToId: 'Continue to ID Check',
      demoComplete: 'Demo mode complete. In production, you would be redirected.',
      improveLighting: 'Improve Lighting',
      improveLightingHint: 'Move to a brighter area to get accurate results',
    },
    documentSelect: {
      title: 'Select Document Type',
      description: "Choose the type of ID document you'll scan",
      idCard: 'National ID Card',
      idCardHint: 'Government-issued ID',
      passport: 'Passport',
      passportHint: 'Photo page only',
      driversLicense: "Driver's License",
      driversLicenseHint: 'Valid driving license',
    },
    captureMode: {
      captureFrontSide: 'Capture Front Side',
      captureBackSide: 'Capture Back Side',
      chooseMethod: 'Choose how to capture your document',
      useCamera: 'Use Camera',
      useCameraHint: 'Take a photo of your document',
      uploadFile: 'Upload File',
      uploadFileHint: 'Select from your device',
    },
    documentCamera: {
      frontSide: 'Front Side',
      backSide: 'Back Side',
      positionInFrame: 'Position document in frame',
      scanFront: 'Scan Front of Document',
      scanBack: 'Scan Back of Document',
      keepFlat: 'Keep the document flat and well-lit',
      capturePhoto: 'Capture Photo',
    },
    documentPreview: {
      frontPreview: 'Front Preview',
      backPreview: 'Back Preview',
      lookingGood: 'Looking Good!',
      checkDetails: 'Make sure all details are clearly visible',
      imageQuality: 'Image quality',
      qualityGood: 'Good',
      usePhoto: 'Use This Photo',
      retakePhoto: 'Retake Photo',
    },
    documentCapture: {
      captureFront: 'Capture Front Side',
      captureBack: 'Capture Back Side',
      alignDocument: 'Align the document within the frame',
      captureButton: 'Capture',
      retakeButton: 'Retake',
      confirmButton: 'Confirm',
      frontSide: 'Front Side',
      backSide: 'Back Side',
      positionDocument: 'Position your document in the frame',
      alignWithFrame: 'Align document with the frame',
      positionFrontId: 'Position front of ID',
      positionBackId: 'Position back of ID',
      scanFrontId: 'Scan Front of ID',
      scanBackId: 'Scan Back of ID',
      positionWithinFrame: 'Position the front side within the frame',
      flipAndScan: 'Flip your ID and scan the back',
      goodLight: 'Good light',
      flatSurface: 'Flat surface',
      frontCaptured: 'Front captured',
      captureIdCard: 'Capture ID Card',
      capturePassport: 'Capture Passport',
      captureDriversLicense: "Capture Driver's License",
    },
    processing: {
      title: 'Processing',
      description: 'Please wait while we process',
      analyzingDocument: 'Analyzing document...',
      almostDone: 'Almost done...',
      faceVerified: 'Face verified',
      livenessConfirmed: 'Liveness confirmed',
      processingDocuments: 'Processing documents...',
    },
    success: {
      title: 'Age Check Passed',
      description: 'Age & liveness check complete',
      redirecting: 'Redirecting...',
      summary: 'Summary',
      faceMatch: 'Face Match',
      liveness: 'Liveness',
      age: 'Age',
      idDocument: 'ID Document',
      pass: 'Pass',
      fail: 'Fail',
      valid: 'Valid',
      notApplicable: 'N/A',
      done: 'Done',
      feedbackTitle: 'Please rate your experience',
      feedbackPlaceholder: 'Tell us how we can improve...',
      skip: 'Skip',
      submit: 'Submit',
    },
    failure: {
      title: 'Age Check Failed',
      description: 'We could not complete the age check. Please try again.',
      tryAgainButton: 'Try Again',
      leaveButton: 'Leave',
      attemptsLeft: '{{count}} attempt(s) left',
      noAttemptsLeft: 'No more attempts available',
    },
    error: {
      title: 'Configuration Required',
      description:
        'Some required parameters are missing from the URL. Please contact the website administrator.',
      missingParams: 'Missing Parameters',
      requiredParams: 'Required Parameters',
      technicalDetails: 'Technical Details',
      urlMustInclude: 'URL must include these query parameters:',
    },
    underage: {
      title: 'Age Requirement Not Met',
      description: 'You must be at least {{minAge}} years old to access this service.',
    },
    consent: {
      title: 'Help Improve Age Check',
      description:
        'Allow AppBouncer to collect a small amount of anonymised usage data (such as feed capture) to improve age checks and fallback accuracy. Your data is never sold or provided to 3rd parties.',
      allowButton: 'Allow',
    },
    documentConsent: {
      title: 'Document Required',
      description:
        'To complete the age check, we need to verify your age using an official document. Do you consent to upload your ID document?',
      allowButton: 'Continue with Document',
      declineButton: 'Decline',
      privacyNote: 'Your document will be processed securely and deleted after the age check.',
    },
    selfDeclaration: {
      title: 'Your Date of Birth',
      description: 'Please enter your date of birth to continue',
      day: 'Day',
      month: 'Month',
      year: 'Year',
      yourAge: 'Your age:',
      years: 'years',
      confirmButton: 'Continue',
      months: {
        january: 'January',
        february: 'February',
        march: 'March',
        april: 'April',
        may: 'May',
        june: 'June',
        july: 'July',
        august: 'August',
        september: 'September',
        october: 'October',
        november: 'November',
        december: 'December',
      },
    },
  },

  lighting: {
    warning: 'Lighting Issue',
    adjust: 'Adjust your lighting',
    lowLight: 'Low light detected',
    tooBright: 'Too bright',
    poorContrast: 'Poor contrast',
    improveLighting: 'Move to better lighting',
    moveToLighter: 'Move to a brighter area',
    videoNotReady: 'Video not ready',
  },

  errors: {
    cameraNotFound: 'No camera found on this device',
    cameraPermissionDenied: 'Camera permission was denied',
    cameraInUse: 'Camera is in use by another app',
    cameraNotSupported: 'Camera does not support required settings',
    cameraSecurityError: 'Camera requires HTTPS connection',
    networkError: 'Network error. Please check your connection.',
    timeout: 'Request timed out. Please try again.',
    invalidApiKey: 'Invalid API key',
    sessionExpired: 'Session expired. Please start over.',
    verificationFailed: 'Age check failed. Please try again.',
    unknownError: 'An unexpected error occurred',
    cameraAccessDeniedDetailed: 'Please allow camera access to continue.',
    cameraNotFoundDetailed: 'No camera was detected. Please connect a camera and try again.',
    cameraInUseDetailed: 'The camera is being used by another app. Please close it and try again.',
    cameraOverconstrained:
      'Camera does not support the requested resolution. Trying with default settings...',
    networkTimeout: 'The request took too long. Please check your connection and try again.',
    networkOffline: 'You appear to be offline. Please check your internet connection.',
    apiGenericError: 'Something went wrong. Please try again.',
    faceNotDetected: 'Please position your face in the frame.',
    livenessFailed: 'Liveness check failed. Please try again.',
    documentCaptureFailed: 'Could not capture document. Please ensure good lighting and try again.',
    initializationFailed: 'Failed to load. Please refresh and try again.',
    modelLoadFailed: 'Failed to load required components. Please refresh and try again.',
    unexpectedError: 'An unexpected error occurred. Please try again.',
    configError: 'Configuration error. Please contact support.',
  },

  steps: {
    faceDetection: 'Face Detection',
    liveness: 'Liveness Check',
    age: 'Age Check',
    documentScan: 'Document Scan',
    selfDeclaration: 'Self Declaration',
  },

  status: {
    cameraStarted: 'Camera started',
    cameraReady: 'Camera ready',
    cameraAccessDenied: 'Camera access denied',
    cameraError: 'Camera error',
    cameraNotAvailable: 'Camera not available',
    noFaceDetected: 'No face detected',
    livenessVerified: 'Liveness verified',
    livenessCheckFailed: 'Liveness check failed',
    verifyingAge: 'Verifying age...',
    processingDocument: 'Processing document...',
    documentProcessed: 'Document processed successfully',
    verificationCancelled: 'Age check cancelled',
    improveLightingForAge: 'Improve lighting to get accurate results',
    getReady: 'Get Ready',
    preparingLiveness: 'Preparing liveness check...',
    uploadingSelfie: 'Uploading selfie images...',
    preparingUpload: 'Preparing upload...',
    uploadingFront: 'Uploading front document...',
    uploadingBack: 'Uploading back document...',
    processingDocuments: 'Processing documents...',
    documentSubmissionFailed: 'Document submission failed. Please try again.',
    selectJpegOrPng: 'Please select a JPEG or PNG image',
    fileTooLarge: 'File too large. Maximum 5MB allowed.',
    errorReadingFile: 'Error reading file',
    frontDocumentRequired: 'Front document image required',
    verificationFailedClose: 'Age check failed. You will be redirected back.',
    verificationCompleteClose: 'Age check complete! You will be redirected back.',
    analyzingFace: 'Analyzing face...',
  },
};
